import { get } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Menu, IconButton as RNIconButton } from 'react-native-paper';
import { getActions, getItemListClick } from '../../../shared';
import { createStyle } from '../style';
import {
  CHAIN_IDS,
  JPYC_ADDRESSES,
  JPYCTransferProps,
  NetworkMeta,
  NETWORKS,
} from '../type';
import { useJpycBindingValue } from '../useBindingValue.hook';
import { ErrorForm } from './ErrorForm';
import { ProgressingForm } from './ProgressingForm';
import { SuccessForm } from './SuccessForm';
import { useTransfer } from './useTransfer';
import { useWallet } from './useWallet';

const DEFAULT_NETWORK = CHAIN_IDS.POLYGON;

export const JPYCtransferForm = (props: JPYCTransferProps) => {
  const bindingValue = useJpycBindingValue(props);

  const titleText = get(bindingValue, 'title.text', 'お支払い金額');
  const paymentButtonText = get(bindingValue, 'paymentButton.text', '次へ');
  const amount = Number(get(bindingValue, 'amount', 0)) || 0;
  const recipientAddress = get(bindingValue, 'recipientAddress') || '';
  const useGasless =
    get(bindingValue, 'useGasless', false).toString() === 'true';

  const { provider, account, connectWallet, switchOrAddChain, loadingChain } =
    useWallet();
  const { error, isProcessing, transfer, txHash, resetState, gaslessTransfer } =
    useTransfer();
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkMeta>();
  const [initialized, setInitialized] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);

  const openMenu = () => setMenuVisible(true);
  const closeMenu = () => setMenuVisible(false);
  const handleSelectNetwork = (value: CHAIN_IDS) => {
    const network = NETWORKS[value];
    if (!network) {
      return;
    }
    if (network.chainId !== selectedNetwork?.chainId) {
      switchOrAddChain(network.chainId);
    }
    setSelectedNetwork(network);
    closeMenu();
  };

  const handleAction = (
    actionType: 'successActions' | 'failedActions',
    txHash?: string
  ) => {
    props.onPress &&
      props.onPress(getActions(props, actionType), {
        itemListClick: getItemListClick(props.record),
        customActions: {
          [props.id]: {
            transactionHash: txHash || '',
            senderAddress: account,
            network: selectedNetwork?.label,
          },
        },
      });
  };

  const chainOptions = Object.values(NETWORKS).map((network) => ({
    value: network.chainId,
    label: network.label,
  }));

  const styles = createStyle(props);

  // initialize chain
  useEffect(() => {
    if (!initialized) {
      handleSelectNetwork(DEFAULT_NETWORK);
      setInitialized(true);
    }
  }, [initialized]);

  if (isProcessing) {
    return <ProgressingForm jpycElementProps={props} />;
  }

  if (txHash) {
    return (
      <SuccessForm
        txHash={txHash}
        amount={amount}
        chainId={selectedNetwork!.chainId}
        onBack={() => {
          resetState();
        }}
        jpycElementProps={props}
      />
    );
  }

  if (error) {
    return (
      <ErrorForm
        errorMessage={error}
        onBack={() => {
          resetState();
        }}
        jpycElementProps={props}
      />
    );
  }

  // payment form
  return (
    <View style={styles.container}>
      <View style={styles.iconWrapper}>
        <View style={styles.iconCircle}>
          <RNIconButton icon="wallet" onPress={() => {}} color="#ffffff" />
        </View>
      </View>

      <View style={styles.headerBlock}>
        <Text style={styles.title}>{titleText}</Text>
        <Text style={styles.subtitle}>
          支払い期限：{moment().add(1, 'hour').format('YYYY/MM/DD HH:mm')}
        </Text>
      </View>

      <View style={styles.amountRow}>
        <Text style={styles.amountValue}>
          {Number.isFinite(amount) ? amount : 0}
        </Text>
        <Text style={styles.amountUnit}>JPYC</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>ネットワーク</Text>
        <View
          style={styles.selectContainer}
          nativeID="network-select-container"
        >
          <Menu
            visible={menuVisible}
            onDismiss={closeMenu}
            anchor={
              <TouchableOpacity
                style={[
                  styles.selectAnchor,
                  {
                    width: props.style?.width - 100,
                    flexDirection: 'row',
                    alignItems: 'center',
                  },
                ]}
                onPress={openMenu}
              >
                <Image
                  source={{ uri: selectedNetwork?.iconUrl }}
                  style={{ width: 20, height: 20 }}
                />
                <Text style={styles.selectText}>{selectedNetwork?.label}</Text>
              </TouchableOpacity>
            }
          >
            {chainOptions.map((opt) => (
              <Menu.Item
                key={opt.value}
                onPress={() => handleSelectNetwork(opt.value as CHAIN_IDS)}
                style={{ width: props.style?.width - 100 }}
                title={
                  <View
                    style={{
                      width: props.style?.width - 100,
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Image
                      source={{ uri: NETWORKS[opt.value as CHAIN_IDS].iconUrl }}
                      style={{ width: 20, height: 20 }}
                    />
                    <Text style={{ marginLeft: 8 }}>{opt.label}</Text>
                  </View>
                }
              />
            ))}
          </Menu>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>送信先アドレス</Text>
        <View style={styles.addressBox}>
          <Text
            style={styles.addressText}
            numberOfLines={1}
            ellipsizeMode="middle"
          >
            {recipientAddress || '—'}
          </Text>
        </View>
      </View>

      <TouchableOpacity
        style={styles.button}
        disabled={loadingChain}
        onPress={async () => {
          if (!account || !provider) {
            connectWallet();
            return;
          }
          let result;
          if (useGasless) {
            result = await gaslessTransfer({
              recipient: recipientAddress,
              amount: amount.toString(),
              tokenAddress:
                JPYC_ADDRESSES[selectedNetwork?.chainId ?? DEFAULT_NETWORK],
              signer: provider?.getSigner() || null,
              relayerEndpoint: props.relayerEndpoint,
              deadline: moment().add(1, 'hour').unix(),
            });
          } else {
            result = await transfer({
              amount: amount.toString(),
              recipient: recipientAddress,
              assetType: 'erc20',
              signer: provider?.getSigner() || null,
              tokenAddress:
                JPYC_ADDRESSES[selectedNetwork?.chainId ?? DEFAULT_NETWORK],
              decimalsOverride: 18, // constant
            });
          }

          if (result.error) {
            handleAction('failedActions', result.txHash);
            alert(result.error);
            return;
          }

          handleAction('successActions', result.txHash);
        }}
        activeOpacity={0.85}
      >
        {loadingChain ? (
          <ActivityIndicator size="small" color="#ffffff" />
        ) : (
          <Text style={styles.buttonText}>{paymentButtonText}</Text>
        )}
      </TouchableOpacity>
    </View>
  );
};
